/**
 * conversationStreamStore - Zustand store for conversation SSE connections
 *
 * Manages SSE streaming state outside of React's component lifecycle.
 * This allows conversation streams to continue running in the background when
 * switching tabs, preventing message loss from aborted connections.
 *
 * Key design:
 * - Each conversation has its own entry in a Map keyed by `storeKey`
 * - `storeKey` comes from Tab.storeKey - a UUID generated when tab opens
 * - `storeKey` stays stable across draft→saved transition (no remount)
 * - AbortController stored in the store, not a React ref
 * - Cleanup called when tab closes (not on component unmount)
 *
 * Note: For simpler background task stores, see createBackgroundTaskStore.ts
 * which provides a reusable factory pattern. This conversation store has
 * additional conversation-specific methods beyond the base pattern.
 */

import { create } from 'zustand';
import type { IntegrationProposal, Message, StatusLine } from '../hooks/useConversation';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface PendingConversationMessage {
  message: string;
}

export interface ConversationStreamState {
  messages: Message[];
  isLoading: boolean;
  error: Error | undefined;
  activeStatusLines: StatusLine[];
  conversationName: string;
  isLoadingConversation: boolean;
  abortController: AbortController | null;
  // The workbook this turn built, for its link card
  createdWorkbook: { id: string; name: string } | null;
  // Track the most recently created presentation (deck) for rendering the link card
  createdPresentation: { id: string; name: string; slideCount: number } | null;
  // Track the most recently created page for rendering the link card
  createdPage: { id: string; name: string } | null;
  // The configuration change the agent proposed this turn, if any — rendered
  // as the inline approval card until the turn finishes and it becomes a
  // persisted message part (docs/projects/index.md § The envelope tools).
  proposal: IntegrationProposal | null;
  // Pending message to auto-submit when the conversation viewer mounts
  pendingMessage: PendingConversationMessage | null;
}

interface ConversationStreamStore {
  conversations: Map<string, ConversationStreamState>;

  // Initialize or get conversation state
  initConversation(key: string): ConversationStreamState;
  getConversation(key: string): ConversationStreamState | undefined;

  // Message management
  setMessages(key: string, messages: Message[]): void;
  addMessage(key: string, message: Message): void;
  updateLastAssistantMessage(key: string, content: string, msgId: string): void;
  // Persist activeStatusLines onto the last assistant message (called when streaming completes)
  finalizeLastAssistantMessage(key: string): void;

  // Loading states
  setIsLoading(key: string, isLoading: boolean): void;
  setIsLoadingConversation(key: string, isLoading: boolean): void;
  setError(key: string, error: Error | undefined): void;

  // Conversation metadata
  setConversationName(key: string, name: string): void;

  // Status lines (flat list, no hierarchy)
  setActiveStatusLines(key: string, statusLines: StatusLine[]): void;
  addStatusLine(key: string, statusLine: StatusLine): void;
  updateStatusLine(key: string, statusLineId: string, updates: Partial<StatusLine>): void;

  // Abort controller management
  setAbortController(key: string, controller: AbortController | null): void;
  abortStream(key: string): void;

  // Created workbook tracking
  setCreatedWorkbook(key: string, workbook: { id: string; name: string } | null): void;

  // Created presentation (deck) tracking
  setCreatedPresentation(
    key: string,
    presentation: { id: string; name: string; slideCount: number } | null,
  ): void;

  // Created page tracking
  setCreatedPage(key: string, page: { id: string; name: string } | null): void;

  // The turn's proposed configuration change (the inline approval card)
  setProposal(key: string, proposal: IntegrationProposal | null): void;

  // Pending message (set before navigating to /conversation/new, consumed on mount)
  setPendingMessage(key: string, pending: PendingConversationMessage | null): void;

  // Cleanup
  cleanupConversation(key: string): void;
  clearStreamingState(key: string): void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Default state factory
// ─────────────────────────────────────────────────────────────────────────────

function createDefaultState(): ConversationStreamState {
  return {
    messages: [],
    isLoading: false,
    error: undefined,
    activeStatusLines: [],
    conversationName: 'New Conversation',
    isLoadingConversation: false,
    abortController: null,
    createdWorkbook: null,
    createdPresentation: null,
    createdPage: null,
    proposal: null,
    pendingMessage: null,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Store implementation
// ─────────────────────────────────────────────────────────────────────────────

export const useConversationStreamStore = create<ConversationStreamStore>((set, get) => ({
  conversations: new Map(),

  initConversation: (key: string) => {
    const existing = get().conversations.get(key);
    if (existing) {
      return existing;
    }

    const newState = createDefaultState();
    set((state) => {
      const newMap = new Map(state.conversations);
      newMap.set(key, newState);
      return { conversations: newMap };
    });
    return newState;
  },

  getConversation: (key: string) => {
    return get().conversations.get(key);
  },

  setMessages: (key: string, messages: Message[]) => {
    set((state) => {
      const conv = state.conversations.get(key);
      if (!conv) return state;

      const newMap = new Map(state.conversations);
      newMap.set(key, { ...conv, messages });
      return { conversations: newMap };
    });
  },

  addMessage: (key: string, message: Message) => {
    set((state) => {
      const conv = state.conversations.get(key);
      if (!conv) return state;

      const newMap = new Map(state.conversations);
      newMap.set(key, { ...conv, messages: [...conv.messages, message] });
      return { conversations: newMap };
    });
  },

  updateLastAssistantMessage: (key: string, content: string, msgId: string) => {
    set((state) => {
      const conv = state.conversations.get(key);
      if (!conv) return state;

      const messages = conv.messages;
      const last = messages[messages.length - 1];

      let newMessages: Message[];
      if (last?.id === msgId) {
        newMessages = [
          ...messages.slice(0, -1),
          { ...last, content },
        ];
      } else {
        newMessages = [
          ...messages,
          {
            id: msgId,
            role: 'assistant',
            content,
            createdAt: new Date(),
          },
        ];
      }

      const newMap = new Map(state.conversations);
      newMap.set(key, { ...conv, messages: newMessages });
      return { conversations: newMap };
    });
  },

  finalizeLastAssistantMessage: (key: string) => {
    set((state) => {
      const conv = state.conversations.get(key);
      if (!conv) return state;

      const messages = conv.messages;
      const last = messages[messages.length - 1];

      // Only finalize if the last message is from assistant and we have status lines
      if (!last || last.role !== 'assistant' || conv.activeStatusLines.length === 0) {
        return state;
      }

      // Copy activeStatusLines to the message's statusLines field
      const newMessages = [
        ...messages.slice(0, -1),
        { ...last, statusLines: [...conv.activeStatusLines] },
      ];

      const newMap = new Map(state.conversations);
      newMap.set(key, { ...conv, messages: newMessages });
      return { conversations: newMap };
    });
  },

  setIsLoading: (key: string, isLoading: boolean) => {
    set((state) => {
      const conv = state.conversations.get(key);
      if (!conv) return state;

      const newMap = new Map(state.conversations);
      newMap.set(key, { ...conv, isLoading });
      return { conversations: newMap };
    });
  },

  setIsLoadingConversation: (key: string, isLoadingConversation: boolean) => {
    set((state) => {
      const conv = state.conversations.get(key);
      if (!conv) return state;

      const newMap = new Map(state.conversations);
      newMap.set(key, { ...conv, isLoadingConversation });
      return { conversations: newMap };
    });
  },

  setError: (key: string, error: Error | undefined) => {
    set((state) => {
      const conv = state.conversations.get(key);
      if (!conv) return state;

      const newMap = new Map(state.conversations);
      newMap.set(key, { ...conv, error });
      return { conversations: newMap };
    });
  },

  setConversationName: (key: string, name: string) => {
    set((state) => {
      const conv = state.conversations.get(key);
      if (!conv) return state;

      const newMap = new Map(state.conversations);
      newMap.set(key, { ...conv, conversationName: name });
      return { conversations: newMap };
    });
  },

  setActiveStatusLines: (key: string, statusLines: StatusLine[]) => {
    set((state) => {
      const conv = state.conversations.get(key);
      if (!conv) return state;

      const newMap = new Map(state.conversations);
      newMap.set(key, { ...conv, activeStatusLines: statusLines });
      return { conversations: newMap };
    });
  },

  addStatusLine: (key: string, statusLine: StatusLine) => {
    set((state) => {
      const conv = state.conversations.get(key);
      if (!conv) return state;

      const newMap = new Map(state.conversations);
      newMap.set(key, { ...conv, activeStatusLines: [...conv.activeStatusLines, statusLine] });
      return { conversations: newMap };
    });
  },

  updateStatusLine: (key: string, statusLineId: string, updates: Partial<StatusLine>) => {
    set((state) => {
      const conv = state.conversations.get(key);
      if (!conv) return state;

      const newStatusLines = conv.activeStatusLines.map((sl) =>
        sl.id === statusLineId ? { ...sl, ...updates } : sl
      );

      const newMap = new Map(state.conversations);
      newMap.set(key, { ...conv, activeStatusLines: newStatusLines });
      return { conversations: newMap };
    });
  },

  setAbortController: (key: string, controller: AbortController | null) => {
    set((state) => {
      const conv = state.conversations.get(key);
      if (!conv) return state;

      const newMap = new Map(state.conversations);
      newMap.set(key, { ...conv, abortController: controller });
      return { conversations: newMap };
    });
  },

  abortStream: (key: string) => {
    const conv = get().conversations.get(key);
    if (conv?.abortController) {
      conv.abortController.abort();
      set((state) => {
        const c = state.conversations.get(key);
        if (!c) return state;

        const newMap = new Map(state.conversations);
        newMap.set(key, { ...c, abortController: null, isLoading: false });
        return { conversations: newMap };
      });
    }
  },

  setCreatedWorkbook: (key: string, workbook: { id: string; name: string } | null) => {
    set((state) => {
      const conv = state.conversations.get(key);
      if (!conv) return state;

      const newMap = new Map(state.conversations);
      newMap.set(key, { ...conv, createdWorkbook: workbook });
      return { conversations: newMap };
    });
  },

  setCreatedPresentation: (
    key: string,
    presentation: { id: string; name: string; slideCount: number } | null,
  ) => {
    set((state) => {
      const conv = state.conversations.get(key);
      if (!conv) return state;

      const newMap = new Map(state.conversations);
      newMap.set(key, { ...conv, createdPresentation: presentation });
      return { conversations: newMap };
    });
  },

  setCreatedPage: (key: string, page: { id: string; name: string } | null) => {
    set((state) => {
      const conv = state.conversations.get(key);
      if (!conv) return state;

      const newMap = new Map(state.conversations);
      newMap.set(key, { ...conv, createdPage: page });
      return { conversations: newMap };
    });
  },

  setProposal: (key: string, proposal: IntegrationProposal | null) => {
    set((state) => {
      const conv = state.conversations.get(key);
      if (!conv) return state;

      const newMap = new Map(state.conversations);
      newMap.set(key, { ...conv, proposal });
      return { conversations: newMap };
    });
  },

  setPendingMessage: (key: string, pending: PendingConversationMessage | null) => {
    set((state) => {
      const conv = state.conversations.get(key);
      if (!conv) return state;

      const newMap = new Map(state.conversations);
      newMap.set(key, { ...conv, pendingMessage: pending });
      return { conversations: newMap };
    });
  },

  cleanupConversation: (key: string) => {
    // Abort any active stream first
    const conv = get().conversations.get(key);
    if (conv?.abortController) {
      conv.abortController.abort();
    }

    // Remove from store
    set((state) => {
      const newMap = new Map(state.conversations);
      newMap.delete(key);
      return { conversations: newMap };
    });
  },

  clearStreamingState: (key: string) => {
    set((state) => {
      const conv = state.conversations.get(key);
      if (!conv) return state;

      const newMap = new Map(state.conversations);
      newMap.set(key, {
        ...conv,
        activeStatusLines: [],
      });
      return { conversations: newMap };
    });
  },
}));

// ─────────────────────────────────────────────────────────────────────────────
// Selector hooks for efficient subscriptions
// ─────────────────────────────────────────────────────────────────────────────

export function useConversationMessages(key: string): Message[] {
  return useConversationStreamStore((state) => state.conversations.get(key)?.messages ?? []);
}

export function useConversationLoading(key: string): boolean {
  return useConversationStreamStore((state) => state.conversations.get(key)?.isLoading ?? false);
}

export function useConversationError(key: string): Error | undefined {
  return useConversationStreamStore((state) => state.conversations.get(key)?.error);
}

export function useConversationStatusLines(key: string): StatusLine[] {
  return useConversationStreamStore((state) => state.conversations.get(key)?.activeStatusLines ?? []);
}

export function useConversationName(key: string): string {
  return useConversationStreamStore((state) => state.conversations.get(key)?.conversationName ?? 'New Conversation');
}

export function useConversationLoadingState(key: string): boolean {
  return useConversationStreamStore((state) => state.conversations.get(key)?.isLoadingConversation ?? false);
}

export function useCreatedWorkbook(key: string): { id: string; name: string } | null {
  return useConversationStreamStore((state) => state.conversations.get(key)?.createdWorkbook ?? null);
}

export function useCreatedPresentation(
  key: string,
): { id: string; name: string; slideCount: number } | null {
  return useConversationStreamStore(
    (state) => state.conversations.get(key)?.createdPresentation ?? null,
  );
}

export function useCreatedPage(key: string): { id: string; name: string } | null {
  return useConversationStreamStore((state) => state.conversations.get(key)?.createdPage ?? null);
}

export function useProposal(key: string): IntegrationProposal | null {
  return useConversationStreamStore((state) => state.conversations.get(key)?.proposal ?? null);
}
